import React, { useState, useEffect, useRef } from "react";
import WaveSurfer from "wavesurfer.js";
import { Task } from "@induz/sdk";
import "./audioPlayer.scss";
import useTheme from "hooks/observer";
import useScreenMobile from "hooks/useScreen";
import { Icon } from "@iconify/react";

// Assets
import logo from "assets/images/homepage/main/logo.svg";
import logoDark from "assets/images/homepage/main/logoDark.svg";

// Icon Components
const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SUPPORTED_FORMATS = {
    mp3: "audio/mpeg",
    wav: "audio/wav",
    ogg: "audio/ogg",
    m4a: "audio/mp4",
    flac: "audio/flac",
    aac: "audio/mp4",
    m4b: "audio/mp4"
};

const getAudioMimeType = (extension) => {
    return SUPPORTED_FORMATS[extension] || null;
};

const WaveformVisualizer = ({ analyserNode, theme, isPlaying }) => {
    const canvasRef = useRef(null);
    const requestRef = useRef();

    useEffect(() => {
        if (!analyserNode) return;

        const bufferLength = analyserNode.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
            if (!canvasRef.current) return;
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            const width = canvas.width;
            const height = canvas.height;

            analyserNode.getByteFrequencyData(dataArray);
            ctx.clearRect(0, 0, width, height);

            const barWidth = 3;
            const barGap = 4;
            const centerX = width / 2;
            const centerHeight = height / 2;

            const maxBarsOnSide = Math.floor(centerX / (barWidth + barGap));
            const barColor = "#9c3de7";
            const shadowColor = "#67308f";

            ctx.fillStyle = barColor;
            ctx.shadowBlur = 8;
            ctx.shadowColor = shadowColor;

            for (let i = 0; i < maxBarsOnSide; i++) {
                const binIdx = Math.floor((i / maxBarsOnSide) * (bufferLength / 4));
                const value = dataArray[binIdx];
                const percent = value * 1.2 / 255;
                const barHeight = Math.max(4, percent * height * 0.85);
                const y = centerHeight - (barHeight / 2);

                const xRight = centerX + i * (barWidth + barGap);
                ctx.beginPath();
                ctx.roundRect(xRight, y, barWidth, barHeight, barWidth / 2);
                ctx.fill();

                const xLeft = centerX - (i + 1) * (barWidth + barGap);
                ctx.beginPath();
                ctx.roundRect(xLeft, y, barWidth, barHeight, barWidth / 2);
                ctx.fill();
            }

            requestRef.current = requestAnimationFrame(draw);
        };

        requestRef.current = requestAnimationFrame(draw);

        return () => cancelAnimationFrame(requestRef.current);
    }, [analyserNode, theme]);

    return (
        <div className="visualizer-container">
            <canvas ref={canvasRef} width="600" height="200" className="visualizer-canvas" />
        </div>
    );
};

const AudioViewer = ({ isVisible, handleCancel, fo, fileName, dupCurrentPath }) => {
    const [loading, setLoading] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [retryTrigger, setRetryTrigger] = useState(0);
    const [isLooping, setIsLooping] = useState(false);
    const theme = useTheme();
    const isMobileView = useScreenMobile({ size: 768 });

    const abortControllerRef = useRef(null);
    const mediaSourceRef = useRef(null);
    const sourceBufferRef = useRef(null);

    // Ordered chunks management
    const fetchedChunksRef = useRef({}); // offset -> ArrayBuffer
    const nextAppendOffsetRef = useRef(0);
    const nextFetchOffsetRef = useRef(0);

    const wavesurferRef = useRef(null);
    const waveContainerRef = useRef(null);
    const audioRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const isGraphConnectedRef = useRef(false); // Track graph connection state
    const fetchIdRef = useRef(0);
    const totalSizeRef = useRef(0);

    const isEvictingRef = useRef(false);
    const isClearingBufferRef = useRef(false);
    const isMseModeRef = useRef(true);
    const wasPlayingRef = useRef(false);
    const durationLockRef = useRef(false);
    const forceNativeRef = useRef(false);
    const lastFileRef = useRef(null);

    // Persistence of audio context
    useEffect(() => {
        if (!audioContextRef.current) {
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            audioContextRef.current = new AudioContext();
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 512;
        }
        return () => {
            if (audioContextRef.current && audioContextRef.current.state !== "closed") {
                audioContextRef.current.close().catch(console.error);
                audioContextRef.current = null;
                analyserRef.current = null;
                sourceNodeRef.current = null;
            }
        };
    }, []);

    const connectAudioGraph = () => {
        if (!audioRef.current || !audioContextRef.current || !analyserRef.current || isGraphConnectedRef.current) return;

        const audioElement = audioRef.current;

        // Ensure context is resumed
        if (audioContextRef.current.state === "suspended") {
            audioContextRef.current.resume().catch(console.error);
        }

        try {
            // Only create source node if it doesn't exist
            if (!sourceNodeRef.current) {
                sourceNodeRef.current = audioContextRef.current.createMediaElementSource(audioElement);
                sourceNodeRef.current.connect(analyserRef.current);
                analyserRef.current.connect(audioContextRef.current.destination);
                isGraphConnectedRef.current = true;
                // console.log("[AudioAnalysis] System graph connected");
            }
        } catch (err) {
            console.warn("[AudioAnalysis] Connection sync note:", err.message);
        }
    };

    const determinePath = (fo, fileName, dupCurrentPath) => {
        const currentDupPath = dupCurrentPath || [];
        let path = fo.getParentPath() + "/" + fo.getName();
        if (currentDupPath.length === 1 && (currentDupPath[0] === "shared" || currentDupPath[0] === "external_shared")) {
            const uuid = typeof fo.getFileUuId === "function" ? fo.getFileUuId() : null;
            path = fo.getParentPath() + "/" + (uuid || fileName);
        }
        return path;
    };

    const extractDuration = async (fo, totSize, filePath, mimeType) => {
        try {
            const chunkSize = 4 * 1024 * 1024;
            const metaTask = new Task(totSize, fileName, "Checking Duration");
            const lastChunkSize = totSize % chunkSize === 0 ? chunkSize : totSize % chunkSize;

            // Fetch first and last 4MB to estimate duration accurately
            const firstChunk = await fo.getChunk(0, filePath, metaTask, chunkSize);
            const lastChunk = await fo.getChunk(totSize - lastChunkSize, filePath, metaTask, lastChunkSize);

            const blob = new Blob([firstChunk, lastChunk], { type: mimeType || fo.type || "audio/mpeg" });
            const tempUrl = URL.createObjectURL(blob);
            const tempAudio = new Audio();
            tempAudio.src = tempUrl;

            return new Promise((resolve) => {
                tempAudio.onloadedmetadata = () => {
                    const dur = tempAudio.duration;
                    URL.revokeObjectURL(tempUrl);
                    resolve(dur && dur !== Infinity ? dur : null);
                };
                tempAudio.onerror = () => {
                    URL.revokeObjectURL(tempUrl);
                    resolve(null);
                };
                setTimeout(() => {
                    URL.revokeObjectURL(tempUrl);
                    resolve(null);
                }, 3000);
            });
        } catch (e) {
            console.error("[DurationExt] Extraction failed:", e);
            setError("[DurationExt] Extraction failed:", e);
            // return null;
        }
    };

    const cleanupBuffer = () => {
        const sb = sourceBufferRef.current;
        const audio = audioRef.current;
        if (!sb || !audio || sb.updating || isEvictingRef.current) return;

        const curTime = audio.currentTime;
        if (curTime > 120) { // Keep 2 minutes of history
            try {
                const removeEnd = curTime - 60;
                let hasBufferBefore = false;
                for (let i = 0; i < sb.buffered.length; i++) {
                    if (sb.buffered.start(i) < removeEnd) {
                        hasBufferBefore = true;
                        break;
                    }
                }
                if (hasBufferBefore) {
                    isEvictingRef.current = true;
                    // console.log(`[MediaSource] Cleaning up old buffer data: 0 to ${removeEnd.toFixed(1)}s`);
                    sb.remove(0, removeEnd);
                }
            } catch (e) {
                console.error("[MediaSource] Eviction error:", e);
            }
        }
    };

    const appendNextAvailable = () => {
        const sb = sourceBufferRef.current;
        const ms = mediaSourceRef.current;
        const audio = audioRef.current;
        if (!sb || !audio || sb.updating || isEvictingRef.current || isClearingBufferRef.current) return;

        const nextOffset = nextAppendOffsetRef.current;
        const chunk = fetchedChunksRef.current[nextOffset];

        if (chunk) {
            try {
                sb.appendBuffer(chunk);
                delete fetchedChunksRef.current[nextOffset];
                nextAppendOffsetRef.current += chunk.byteLength;

                // Stop loading once first data is in
                if (loading) setLoading(false);

                // Track real duration as we append
                if (ms && audio.duration && ms.duration !== Infinity) {
                    // Sync internal state but ignore suspicious jumps (like doubling) if locked
                    const newDur = audio.duration;
                    if (!durationLockRef.current) {
                        if (duration === 0 || (Math.abs(newDur - duration) > 1 && Math.abs(newDur - 2 * duration) > 2)) {
                            // console.log("[Audio] Duration synced from MSE:", newDur);
                            setDuration(newDur);
                        }
                    }
                }

                if (nextAppendOffsetRef.current % (16 * 1024 * 1024) === 0) {
                    const buffered = [];
                    for (let i = 0; i < sb.buffered.length; i++) {
                        buffered.push(`[${sb.buffered.start(i).toFixed(1)}-${sb.buffered.end(i).toFixed(1)}]`);
                    }
                    // console.log(`[MediaSource] Appended: ${nextAppendOffsetRef.current} bytes. Ranges: ${buffered.join(", ")}`);
                    cleanupBuffer();
                }
            } catch (e) {
                if (e.name === "QuotaExceededError") {
                    console.warn("[MediaSource] Buffer Quota hit. Forcing eviction...");
                    cleanupBuffer();
                } else {
                    console.error("[MediaSource] Append error:", e);
                }
            }
        } else if (nextAppendOffsetRef.current >= totalSizeRef.current && ms?.readyState === "open") {
            try {
                ms.endOfStream();
                // Final sync of duration
                if (audio.duration && Math.abs(audio.duration - duration) > 0.1) {
                    setDuration(audio.duration);
                }
                // console.log("[MediaSource] Playback stream finalized.");
            } catch (e) {
                console.error("[MediaSource] EndOfStream error:", e);
            }
        }
    };

    useEffect(() => {
        if (!isVisible || !fo) {
            cleanup();
            return;
        }

        const startStreaming = async () => {
            setLoading(true);
            setError(null);
            setIsInitialized(false);
            setIsPlaying(false);
            cleanup();

            const totSize = await fo.getSize();
            totalSizeRef.current = totSize;

            const fullStreamPath = determinePath(fo, fileName, dupCurrentPath);
            console.log("[AudioStream] Launching:", { fileName, fullStreamPath, totSize });

            const fileExtension = fileName?.split(".").pop()?.toLowerCase();
            const mimeType = getAudioMimeType(fileExtension);

            if (!mimeType) {
                setError(`Unsupported audio format: .${fileExtension}. Only MP3, WAV, OGG, M4A, FLAC, and AAC are supported.`);
                setLoading(false);
                return;
            }

            // Non-blocking duration extraction
            extractDuration(fo, totSize, fullStreamPath, mimeType).then(extractedDur => {
                if (extractedDur && !durationLockRef.current) {
                    setDuration(extractedDur);
                    durationLockRef.current = true;
                    if (mediaSourceRef.current && mediaSourceRef.current.readyState === 'open') {
                        try { mediaSourceRef.current.duration = extractedDur; } catch (e) { console.warn("[MSE] Background duration set error:", e); }
                    }
                    // console.log("[Audio] Duration extracted and locked (bg):", extractedDur);
                }
            }).catch(err => console.error("[Audio] Background duration extraction failed:", err));

            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            nextFetchOffsetRef.current = 0;
            nextAppendOffsetRef.current = 0;
            fetchedChunksRef.current = {};

            if (lastFileRef.current !== fo) {
                forceNativeRef.current = false;
                lastFileRef.current = fo;
            }

            const canPlayMSE = !forceNativeRef.current && window.MediaSource && MediaSource.isTypeSupported(mimeType);
            const tempAudio = document.createElement("audio");
            const canPlayNative = tempAudio.canPlayType(mimeType) !== "";

            if (!canPlayMSE && !canPlayNative) {
                setError(`Codec for .${fileExtension} is not supported by your browser.`);
                setLoading(false);
                return;
            }

            isMseModeRef.current = canPlayMSE;

            if (canPlayMSE) {
                const ms = new MediaSource();
                mediaSourceRef.current = ms;
                const url = URL.createObjectURL(ms);
                setAudioUrl(url);

                ms.addEventListener("sourceopen", async () => {
                    if (ms.sourceBuffers.length > 0) return;

                    // Immediately signal initialization to show visualizer/controls
                    setIsInitialized(true);

                    if (durationLockRef.current && duration > 0) {
                        try {
                            ms.duration = duration;
                        } catch (e) { console.warn("[MSE] Failed to set duration early:", e); }
                    }

                    try {
                        const sb = ms.addSourceBuffer(mimeType);
                        sourceBufferRef.current = sb;

                        sb.addEventListener("updateend", () => {
                            isEvictingRef.current = false;
                            appendNextAvailable();
                        });

                        sb.addEventListener("error", (e) => console.error("[SourceBuffer] Pipe error:", e));

                        fetchIdRef.current++;
                        startConcurrentFetch(totSize, signal, 0, fetchIdRef.current, fullStreamPath);
                    } catch (e) {
                        console.warn("[MediaSource] MSE failed for this codec, falling back to native:", e);
                        forceNativeRef.current = true;
                        cleanup();
                        setRetryTrigger(prev => prev + 1);
                    }
                });
            } else {
                console.log(`[AudioStream] MSE not supported for ${mimeType}. Falling back to full download.`);
                setLoading(true);
                setDownloadProgress(0);
                fetchIdRef.current++;
                const fullBlob = await startConcurrentFetch(totSize, signal, 0, fetchIdRef.current, fullStreamPath, true);
                if (fullBlob && fetchIdRef.current === fetchIdRef.current) {
                    const url = URL.createObjectURL(fullBlob);
                    setAudioUrl(url);
                    setIsInitialized(true);
                    setLoading(false);
                }
            }
        };

        startStreaming();
        return () => {
            cleanup();
        };
    }, [isVisible, fo, fileName, retryTrigger]);

    // Initialize Wavesurfer separately when source is ready
    useEffect(() => {
        if (!isVisible || !audioRef.current || !audioUrl) {
            if (wavesurferRef.current) {
                wavesurferRef.current.destroy();
                wavesurferRef.current = null;
            }
            return;
        }

        if (!wavesurferRef.current) {
            wavesurferRef.current = WaveSurfer.create({
                container: waveContainerRef.current,
                media: audioRef.current,
                waveColor: "#9c3de7",
                progressColor: "#67308f",
                cursorColor: "#9c3de7",
                height: 100,
                barAlign: "bottom",
                backend: "MediaElement",
                autoplay: true,
                // Provide substantial dummy peaks to absolutely prevent Wavesurfer from trying to fetch/decode the MSE blob
                peaks: [new Float32Array(1000).fill(0)],
                fetchParams: { mode: 'no-cors' },
            });

            wavesurferRef.current.on("play", () => {
                // console.log("[Wavesurfer] Play event triggered");
                setIsPlaying(true);
            });
            wavesurferRef.current.on("pause", () => {
                // console.log("[Wavesurfer] Pause event triggered");
                setIsPlaying(false);
            });
            wavesurferRef.current.on("error", (err) => {
                console.error("[Wavesurfer] Internal error:", err);
            });
            wavesurferRef.current.on("finish", () => {
                const audio = audioRef.current;
                if (audio) {
                    audio.currentTime = 0;
                    if (audio.loop) {
                        audio.play().catch(console.error);
                    }
                }
            });
        }

        return () => {
            if (wavesurferRef.current) {
                wavesurferRef.current.destroy();
                wavesurferRef.current = null;
            }
        };
    }, [isVisible, audioUrl, theme]);

    const handleRetry = () => {
        durationLockRef.current = false;
        setRetryTrigger(prev => prev + 1);
    };

    const startConcurrentFetch = async (totSize, signal, startOffset, currentFetchId, path, isFallbackMode = false) => {
        const dataTask = new Task(totSize, fileName, isFallbackMode ? "Downloading Full Audio" : "Streaming Audio");
        const chunkSize = 4 * 1024 * 1024; // 4MB
        const maxConcurrency = 12;

        const accumulatedChunks = isFallbackMode ? {} : null;

        nextFetchOffsetRef.current = startOffset;
        let activeDownloads = 0;
        let errorOccurred = null;
        const fetchPool = [];

        // console.log(`[FetchPool] Active [${currentFetchId}] at offset ${startOffset} with ${maxConcurrency} workers`);

        const downloadChunkWithRetry = async (offset, size, retries = 3) => {
            if (errorOccurred || signal.aborted || currentFetchId !== fetchIdRef.current) return;
            for (let i = 0; i < retries; i++) {
                try {
                    const chunk = await fo.getChunk(offset, path, dataTask, size);
                    if (signal.aborted || currentFetchId !== fetchIdRef.current) return;

                    if (isFallbackMode) {
                        accumulatedChunks[offset] = chunk;
                    } else {
                        fetchedChunksRef.current[offset] = chunk;
                        appendNextAvailable();

                        if (!isInitialized) {
                            setIsInitialized(true);
                            setLoading(false);
                        }
                        setIsSeeking(false);
                        if (wasPlayingRef.current) {
                            const audio = audioRef.current;
                            if (audio) {
                                audio.play().catch(e => console.log("[Audio] Auto-resume after seek:", e.message));
                            }
                        }
                    }
                    return;
                } catch (err) {
                    console.warn(`[FetchPool] Retry ${i + 1} for ${offset}:`, err);
                    if (i === retries - 1) errorOccurred = err;
                    else await new Promise(r => setTimeout(r, Math.min(Math.pow(2, i) * 600, 3000)));
                }
            }
        };

        while (nextFetchOffsetRef.current < totSize && !errorOccurred && !signal.aborted && currentFetchId === fetchIdRef.current) {
            // Buffer management: don't download more than 10 chunks (40MB) ahead of player
            const unappendedCount = Object.keys(fetchedChunksRef.current).length;
            if (unappendedCount > 10) {
                await new Promise(r => setTimeout(r, 600));
                continue;
            }

            while (activeDownloads < maxConcurrency && nextFetchOffsetRef.current < totSize && !signal.aborted) {
                const currentOffset = nextFetchOffsetRef.current;
                const remaining = Math.min(chunkSize, totSize - currentOffset);
                nextFetchOffsetRef.current += chunkSize;
                activeDownloads++;

                const promise = downloadChunkWithRetry(currentOffset, remaining)
                    .finally(() => {
                        activeDownloads--;
                        const index = fetchPool.indexOf(promise);
                        if (index > -1) fetchPool.splice(index, 1);

                        if (isFallbackMode) {
                            const downloaded = Object.keys(accumulatedChunks).length;
                            const total = Math.ceil(totSize / chunkSize);
                            setDownloadProgress(Math.min(100, Math.round((downloaded / total) * 100)));
                        }
                    });
                fetchPool.push(promise);
            }

            if (fetchPool.length > 0) await Promise.race(fetchPool);
        }

        await Promise.allSettled(fetchPool);
        if (errorOccurred) {
            console.error("[FetchPool] Terminated due to errors:", errorOccurred);
            return null;
        }

        if (isFallbackMode) {
            const sortedOffsets = Object.keys(accumulatedChunks).map(Number).sort((a, b) => a - b);
            const blobParts = sortedOffsets.map(offset => accumulatedChunks[offset]);
            const fileExtension = fileName?.split(".").pop()?.toLowerCase();
            return new Blob(blobParts, { type: getAudioMimeType(fileExtension) || fo.type || "audio/mpeg" });
        }
        return null;
    };

    const handleSeeking = async (e) => {
        const audio = e.target;
        const seekTime = audio.currentTime;

        // Capture state immediately at start of seek
        wasPlayingRef.current = !audio.paused;

        // Tolerance for buffered check
        let isBuffered = false;
        for (let i = 0; i < audio.buffered.length; i++) {
            if (seekTime >= audio.buffered.start(i) - 0.5 && seekTime <= audio.buffered.end(i) + 1) {
                isBuffered = true;
                break;
            }
        }
        if (isBuffered || !duration) return;

        // console.log(`[Audio] Seeking to unbuffered ${seekTime.toFixed(1)}s (Resetting fetch pipeline)`);
        setIsSeeking(true);
        fetchIdRef.current++;
        fetchedChunksRef.current = {};

        const sb = sourceBufferRef.current;
        if (sb) {
            if (sb.updating) {
                try { sb.abort(); } catch (e) { console.error("[SourceBuffer] Seek abort error:", e); }
            }

            // Robust clearing of SourceBuffer to avoid stalling on backward seeks
            try {
                if (sb.buffered.length > 0) {
                    isClearingBufferRef.current = true;
                    // Remove ALL buffered content
                    sb.remove(0, duration || 10000);

                    // Wait for the removal to complete before restarting fetch
                    const onUpdateEnd = () => {
                        sb.removeEventListener("updateend", onUpdateEnd);
                        isClearingBufferRef.current = false;
                        restartFetch(seekTime);
                    };
                    sb.addEventListener("updateend", onUpdateEnd);
                    return; // Exit and wait for event
                }
            } catch (e) {
                console.error("[SourceBuffer] Clear error:", e);
                isClearingBufferRef.current = false;
            }
        }

        restartFetch(seekTime);
    };

    const restartFetch = (seekTime) => {
        const targetOffset = Math.floor((seekTime / duration) * totalSizeRef.current);
        const alignedOffset = Math.floor(targetOffset / (4 * 1024 * 1024)) * (4 * 1024 * 1024);

        const signal = abortControllerRef.current.signal;
        const fullStreamPath = determinePath(fo, fileName, dupCurrentPath);

        nextFetchOffsetRef.current = alignedOffset;
        nextAppendOffsetRef.current = alignedOffset;
        startConcurrentFetch(totalSizeRef.current, signal, alignedOffset, fetchIdRef.current, fullStreamPath);
    };

    const cleanup = () => {
        fetchIdRef.current++;
        durationLockRef.current = false;
        if (abortControllerRef.current) abortControllerRef.current.abort();
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
        }
        mediaSourceRef.current = null;
        sourceBufferRef.current = null;
        fetchedChunksRef.current = {};
        nextFetchOffsetRef.current = 0;
        nextAppendOffsetRef.current = 0;
        setIsPlaying(false);
        setIsSeeking(false);
        setIsInitialized(false);
        setIsLooping(false);
        setCurrentTime(0);
        setDuration(0);
        isEvictingRef.current = false;
        isGraphConnectedRef.current = false;
        sourceNodeRef.current = null;
        wasPlayingRef.current = true; // Default to auto-play for new files
        setDownloadProgress(0);
    };

    if (!isVisible) return null;

    return (
        <div className="audio-player-container" data-theme={theme} data-is-mobile={isMobileView}>
            <div className="audio-player-layout" data-theme={theme}>
                <div className="audio-player-header" data-theme={theme}>
                    <div className="header-left">
                        <img src={theme === "dark" ? logoDark : logo} alt="Induz" className="header-logo" />
                    </div>
                    <div className="header-center">
                        <span className="file-name">{fileName}</span>
                    </div>
                    <div className="header-right">
                        <div className="close-btn" onClick={handleCancel} title="Close">
                            <CloseIcon />
                        </div>
                    </div>
                </div>

                <div className="audio-player-content">
                    {error ? (
                        <div className="error-overlay">
                            <div className="error-card">
                                <div className="error-icon">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#FF4D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M12 8V12" stroke="#FF4D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M12 16H12.01" stroke="#FF4D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <h3>Oops! Something went wrong</h3>
                                <p>We couldn`&apos;`t load the requested audio. This might be due to a network issue or the file might be corrupted.</p>

                                <div className="technical-details">
                                    <details>
                                        <summary>Technical Details</summary>
                                        <div className="error-text">{error}</div>
                                    </details>
                                </div>

                                <div className="error-actions">
                                    <button className="error-retry-btn" onClick={handleRetry}>Retry</button>
                                    <button className="error-close-btn" onClick={handleCancel}>Exit Player</button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="player-wrapper" data-theme={theme}>
                            {(loading || isSeeking) && (
                                <div className={`loading-overlay ${!isMseModeRef.current && !isSeeking ? 'fallback-loading' : ''}`} data-theme={theme}>
                                    {isMseModeRef.current || isSeeking ? (
                                        <>
                                            <div className="spinner" data-theme={theme}></div>
                                            <p>{isSeeking ? "Jumping to position..." : "Preparing Stream..."}</p>
                                        </>
                                    ) : (
                                        <div className="circular-progress-wrapper">
                                            <div className="spinner" data-theme={theme}></div>
                                            <p className="loading-text">Downloading...</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {isInitialized && analyserRef.current && (
                                <WaveformVisualizer
                                    analyserNode={analyserRef.current}
                                    theme={theme}
                                    isPlaying={isPlaying}
                                />
                            )}

                            <div className="custom-player-wrapper">
                                <div ref={waveContainerRef} className="wavesurfer-container" />
                                <audio
                                    ref={audioRef}
                                    src={audioUrl || undefined}
                                    crossOrigin="anonymous"
                                    onPlay={() => {
                                        setIsPlaying(true);
                                        wasPlayingRef.current = true;
                                        connectAudioGraph();
                                        if (audioContextRef.current?.state === "suspended") {
                                            audioContextRef.current.resume().catch(e => console.warn("[Audio] Context resume failed:", e));
                                        }
                                    }}
                                    onPause={() => {
                                        setIsPlaying(false);
                                        if (!isSeeking) wasPlayingRef.current = false;
                                    }}
                                    onTimeUpdate={(e) => {
                                        setCurrentTime(e.target.currentTime);
                                        if (Math.floor(e.target.currentTime) % 20 === 0) {
                                            cleanupBuffer();
                                            if (audioContextRef.current?.state === "suspended") {
                                                audioContextRef.current.resume().catch(e => console.warn("[Audio] Periodic resume failed:", e));
                                            }
                                        }
                                    }}
                                    onLoadedMetadata={(e) => {
                                        const newDur = e.target.duration;
                                        if (newDur && newDur !== Infinity) {
                                            if (durationLockRef.current) return;
                                            if (duration === 0 || (Math.abs(newDur - duration) > 1 && Math.abs(newDur - 2 * duration) > 2)) {
                                                setDuration(newDur);
                                                if (newDur > 0) durationLockRef.current = true;
                                            }
                                        }
                                    }}
                                    onSeeking={handleSeeking}
                                    onEnded={(e) => {
                                        e.target.currentTime = 0;
                                        if (e.target.loop) {
                                            e.target.play().catch(console.error);
                                        }
                                    }}
                                />

                                <div className="controls-container">
                                    <div className="main-controls">
                                        <div className="control-btn" onClick={() => wavesurferRef.current?.skip(-15)} title="Rewind 15s">
                                            <Icon icon="mdi:rewind" />
                                        </div>
                                        <div className="control-btn play-pause-btn" onClick={() => wavesurferRef.current?.playPause()} title={isPlaying ? "Pause" : "Play"}>
                                            <Icon icon={isPlaying ? "mdi:pause" : "mdi:play"} />
                                        </div>
                                        <div className="control-btn" onClick={() => wavesurferRef.current?.skip(15)} title="Forward 15s">
                                            <Icon icon="mdi:fast-forward" />
                                        </div>
                                    </div>
                                    <div className="secondary-controls">
                                        <div className="time-display">
                                            <span>{Math.floor(currentTime / 60)}:{Math.floor(currentTime % 60).toString().padStart(2, '0')}</span>
                                            <span> / </span>
                                            <span>{Math.floor(duration / 60)}:{Math.floor(duration % 60).toString().padStart(2, '0')}</span>
                                        </div>
                                        <div className="control-btn" onClick={() => {
                                            const newLoop = !isLooping;
                                            setIsLooping(newLoop);
                                            if (audioRef.current) audioRef.current.loop = newLoop;
                                        }} title="Toggle Loop">
                                            <Icon icon={isLooping ? "mdi:repeat" : "mdi:repeat-off"} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AudioViewer;
